import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import API_BASE_URL from '@/config';

const ReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const resp = await axios.get(`${API_BASE_URL}/api/reports`);
      const list = resp.data?.value || resp.data || [];
      const found = list.find(r => r.id === Number(id));
      setReport(found || null);
    } catch (err) {
      console.error('Error fetching report:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [id]);

  if (!report) return <div>Loading...</div>;

  const fmt = (d) => d ? new Date(d).toLocaleDateString() : '—';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-serif text-2xl sm:text-3xl font-bold">Report #{report.id}</h1>
        </div>
        {/* Mobile Icon-only Refresh Button */}
        <Button
          variant="outline"
          size="icon"
          onClick={fetchReport}
          disabled={loading}
          className="sm:hidden h-9 w-9 flex-shrink-0"
          aria-label="Refresh report"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        {/* Desktop Refresh Button */}
        <Button
          variant="outline"
          onClick={fetchReport}
          disabled={loading}
          className="hidden sm:inline-flex h-10 gap-1.5"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <span className="text-sm font-medium text-muted-foreground">Patient:</span>
              <p className="text-base">{report.patient_name || report.patientName}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-muted-foreground">Report Name:</span>
              <p className="text-base font-medium">{report.report_name || report.reportName || 'Unnamed Report'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-muted-foreground">Visit Date:</span>
              <p className="text-base">{fmt(report.visit_date || report.visitDate)}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-muted-foreground">Upload Date:</span>
              <p className="text-base">{fmt(report.date)}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-muted-foreground">Status:</span>
              <p className="text-base">{report.status || 'Uploaded'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {report.file_url && (
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle>Attached File</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button asChild>
                <a href={report.file_url} target="_blank" rel="noreferrer">Open</a>
              </Button>
              <Button asChild>
                <a href={report.file_url} download>Download</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportDetail;
